import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '../environment/environment.service';
import { catchError, map, Observable, tap } from 'rxjs';
import { ENVIRONMENT_SERVICE_TOKEN } from '@mwe/constants';
import { PriceSheet, PriceSheetSearch } from '@mwe/models';
import { openEmptyTab, renderPdfIntoTab } from '@mwe/utils';
import { TariffSelectionStateService } from '../tariff/selection/tariff-selection-state.service';
import { AccountLogic } from '../account/account.logic';

@Injectable({ providedIn: 'root' })
export class PriceSheetService {
  private readonly http = inject(HttpClient);
  private readonly tariffSelectionStateService = inject(TariffSelectionStateService);
  private readonly accountLogic = inject(AccountLogic);
  private readonly environmentService = inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN);

  private resourceUrl: string;

  constructor() {
    this.resourceUrl = this.environmentService.getApiUrl() + 'api/pricesheet';
    if (this.accountLogic.isLoggedOut()) {
      this.resourceUrl = this.resourceUrl.replace('api', 'api/public');
    }
  }

  searchPriceSheet(priceSheetSearch: PriceSheetSearch): Observable<PriceSheet> {
    const params = new HttpParams()
      .set('isuTariffKey', priceSheetSearch.isuTariffKey)
      .set('deadlineDate', priceSheetSearch.deadlineDate)
      .set('isViennaAddress', priceSheetSearch.isViennaAddress)
      .set('voucherCode', priceSheetSearch.voucherName);
    return this.http.get<PriceSheet>(this.resourceUrl + '/search', { params });
  }

  openPriceSheet(isuTariffKeyOrCategory: string) {
    const newTab = openEmptyTab();
    const matchingPriceSheet =
      this.tariffSelectionStateService.priceSheets?.find(priceSheet => priceSheet.isuTariffKey === isuTariffKeyOrCategory) ||
      this.tariffSelectionStateService.priceSheets?.find(
        priceSheet => priceSheet.category.toLowerCase() === isuTariffKeyOrCategory.toLowerCase(),
      );

    this.getPriceSheetByIds(matchingPriceSheet?.archivId, matchingPriceSheet?.dokumentId)
      .pipe(
        tap(response => {
          renderPdfIntoTab(response, newTab);
        }),
        catchError(() => (newTab.document.body.innerHTML = `<p>Das Dokumente konnte nicht geladen werden.</p>`)),
      )
      .subscribe();
  }

  getPriceSheetByIds(archivId: string, priceSheetId: string): Observable<string> {
    const params = new HttpParams().set('archivId', archivId).set('priceSheetId', priceSheetId);
    return this.http.get(this.resourceUrl, { params: params, responseType: 'text' });
  }

  hasMatchingPriceSheet(isuTariffKey: string): boolean {
    return !!this.tariffSelectionStateService.priceSheets?.find(priceSheet => priceSheet?.isuTariffKey === isuTariffKey);
  }

  hasMatchingPriceSheet$(isuTariffKey: string): Observable<boolean> {
    return this.tariffSelectionStateService
      .priceSheetObserver()
      .pipe(map(priceSheets => !!priceSheets?.find(ps => ps.isuTariffKey === isuTariffKey)));
  }
}
